import { useEffect, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import { Button, StatusBadge, cn } from '@taki/ui';
import champsDeclares from '../../taki.fields.json';

/**
 * LES MORCEAUX DU DOSSIER D'UN ANIMAL (vue de fiche « Dossier de l'animal » de
 * Patients) : lecture du sac, options declarees, champs de lecture, alertes,
 * cartes, onglets. Aucun n'appelle le SDK.
 *
 * LES OPTIONS DES CHAMPS se lisent dans le `taki.fields.json` de Patients,
 * y compris celles des consultations (motif, etape) : le jeu des
 * consultations y est declare comme jeu PARTAGE, identique a celui de
 * Consultations (`pnpm apps:check` refuse tout ecart, `pnpm jeux:aligner` le
 * recopie). Aucune liste n'est recopiee a la main.
 *
 * LES ONGLETS SONT DESSINES ICI parce que `@taki/ui` n'en a pas encore : la
 * zone Canvas le permet (G23), avec les seuls tokens du theme. S'ils servent
 * ailleurs, ils deviennent un composant du socle.
 */

export type Fiche = Record<string, unknown>;

/** Le sac d'une fiche, lu sans rien supposer. */
export function sac(fiche: unknown): Record<string, unknown> {
  if (fiche === null || typeof fiche !== 'object') return {};
  const brut = (fiche as Fiche)['customFields'];
  return brut !== null && typeof brut === 'object' ? (brut as Record<string, unknown>) : {};
}

/** L'instant d'une consultation (`performedAt`), quelle que soit sa forme lue. */
export function instantDe(fiche: Fiche): Date | null {
  const brut = fiche['performedAt'];
  if (brut instanceof Date) return brut;
  if (typeof brut === 'string' || typeof brut === 'number') {
    const date = new Date(brut);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
}

export interface OptionDeclaree {
  value: string;
  label: string;
  tone?: string;
}

/** Les options d'un champ a choix d'un jeu, dans l'ordre de la declaration. */
export function optionsDe(type: string, cle: string): readonly OptionDeclaree[] {
  const jeu = champsDeclares.jeux.find((j) => j.type === type);
  const champ = jeu?.champs.find((c) => c.key === cle) as { options?: OptionDeclaree[] } | undefined;
  return champ?.options ?? [];
}

/** L'etape d'une consultation, dans l'ordre du catalogue du champ `stage`. */
export const ETAPES: readonly OptionDeclaree[] = optionsDe('consultation', 'stage');

export type Ton = 'neutral' | 'accent' | 'success' | 'danger';

export function tonDeLEtape(valeur: unknown): Ton {
  const ton = ETAPES.find((o) => o.value === valeur)?.tone;
  return ton === 'accent' || ton === 'success' || ton === 'danger' ? ton : 'neutral';
}

export function libelleDeLEtape(valeur: unknown): string {
  return ETAPES.find((o) => o.value === valeur)?.label ?? 'Sans étape';
}

export function raisonDe(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

/** Une carte plate du bandeau patient : un mot, une valeur. */
export function CarteInfo({ libelle, children }: { libelle: string; children: ReactNode }): JSX.Element {
  return (
    <div className="flex min-w-0 flex-col gap-2 rounded-md border border-border-soft bg-surface px-4 py-3">
      <span className="text-xs text-ink-500">{libelle}</span>
      <div className="min-w-0 text-base font-semibold text-ink-900">{children}</div>
    </div>
  );
}

/** Une section titree ; `actions` se pose a droite du titre (le bouton Protocoles des soins). */
export function Section({ titre, actions, children }: { titre: string; actions?: ReactNode; children: ReactNode }): JSX.Element {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center gap-3 border-b border-border-soft pb-2">
        <h3 className="min-w-0 flex-1 text-base font-semibold text-ink-900">{titre}</h3>
        {actions === undefined ? null : <div className="relative flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
      {children}
    </section>
  );
}

/** Les alertes de l'animal, en rouge, et ses notes internes. */
export function Alertes({ valeurs, notes }: { valeurs: unknown; notes: unknown }): JSX.Element | null {
  const liste = Array.isArray(valeurs) ? valeurs.filter((v): v is string => typeof v === 'string') : [];
  const texte = typeof notes === 'string' ? notes.trim() : '';
  if (liste.length === 0 && texte === '') return null;
  return (
    <div className="flex flex-col gap-2 rounded-md border border-danger-border bg-danger-tint px-4 py-3">
      {liste.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {liste.map((alerte) => (
            <StatusBadge key={alerte} label={alerte} tone="danger" />
          ))}
        </div>
      ) : null}
      {texte !== '' ? <p className="text-sm text-ink-900">{texte}</p> : null}
    </div>
  );
}

/** Un champ ouvert : on ecrit, la sortie du champ enregistre. `lecture` le fige. */
export function ChampTexte({
  libelle,
  valeur,
  onEcrire,
  uneLigne = false,
  lecture = false,
}: {
  libelle: string;
  valeur: unknown;
  onEcrire?: (valeur: string | null) => void;
  uneLigne?: boolean;
  lecture?: boolean;
}): JSX.Element {
  const initial = typeof valeur === 'string' ? valeur : '';
  const [texte, setTexte] = useState(initial);
  useEffect(() => setTexte(initial), [initial]);
  if (lecture) {
    return (
      <div className="flex flex-col gap-1">
        <span className="text-xs font-semibold text-ink-500">{libelle}</span>
        <p className={cn('whitespace-pre-wrap text-sm', initial === '' ? 'text-ink-400' : 'text-ink-900')}>
          {initial === '' ? 'Non renseigné' : initial}
        </p>
      </div>
    );
  }
  const enregistrer = (): void => {
    if (texte === initial) return;
    onEcrire?.(texte.trim() === '' ? null : texte);
  };
  const classes =
    'w-full rounded-sm border border-border bg-surface-1 px-3 py-2 text-sm text-ink-900 placeholder:text-ink-300 ' +
    'focus-visible:outline-none focus-visible:border-accent-border';
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-semibold text-ink-500">{libelle}</span>
      {uneLigne ? (
        <input className={cn(classes, 'h-9 py-0')} value={texte} onChange={(e) => setTexte(e.target.value)} onBlur={enregistrer} />
      ) : (
        <textarea className={cn(classes, 'min-h-20 resize-y')} value={texte} onChange={(e) => setTexte(e.target.value)} onBlur={enregistrer} />
      )}
    </label>
  );
}

/** Un champ a choix, en boutons : un clic enregistre. `lecture` le fige. */
export function Choix({
  libelle,
  options,
  valeur,
  multiple = false,
  lecture = false,
  onEcrire,
}: {
  libelle: string;
  options: readonly OptionDeclaree[];
  valeur: unknown;
  multiple?: boolean;
  lecture?: boolean;
  onEcrire?: (valeur: string | string[] | null) => void;
}): JSX.Element {
  const coches = Array.isArray(valeur) ? (valeur as unknown[]).map(String) : typeof valeur === 'string' ? [valeur] : [];
  if (lecture) {
    return (
      <div className="flex flex-col gap-1">
        <span className="text-xs font-semibold text-ink-500">{libelle}</span>
        <p className={cn('text-sm', coches.length === 0 ? 'text-ink-400' : 'text-ink-900')}>
          {coches.length === 0 ? 'Non renseigné' : coches.join(', ')}
        </p>
      </div>
    );
  }
  const basculer = (v: string): void => {
    if (multiple) onEcrire?.(coches.includes(v) ? coches.filter((x) => x !== v) : [...coches, v]);
    else onEcrire?.(coches.includes(v) ? null : v);
  };
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-semibold text-ink-500">{libelle}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <Button key={o.value} size="sm" variant={coches.includes(o.value) ? 'dark' : 'secondary'} onClick={() => basculer(o.value)}>
            {o.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

export interface Onglet {
  id: string;
  libelle: string;
  /** Un nombre a cote du libelle (lignes, taches ouvertes). */
  compte?: number;
  /** Un point : il reste quelque chose a faire dans l'onglet. */
  point?: boolean;
}

/** La barre d'onglets : un souligne sous l'onglet actif, comme les en-tetes du gabarit. */
export function Onglets({
  onglets,
  actif,
  onChoisir,
}: {
  onglets: readonly Onglet[];
  actif: string;
  onChoisir: (id: string) => void;
}): JSX.Element {
  return (
    <div role="tablist" className="flex gap-1 overflow-x-auto border-b border-border-soft">
      {onglets.map((onglet) => {
        const choisi = onglet.id === actif;
        return (
          <button
            key={onglet.id}
            type="button"
            role="tab"
            aria-selected={choisi}
            onClick={() => onChoisir(onglet.id)}
            className={cn(
              'flex shrink-0 items-center gap-2 border-b-2 px-3 py-3 text-sm font-semibold transition-colors focus-visible:outline-none',
              choisi ? 'border-ink-900 text-ink-900' : 'border-transparent text-ink-500 hover:text-ink-900',
            )}
          >
            {onglet.libelle}
            {onglet.compte !== undefined && onglet.compte > 0 ? (
              <span className="text-xs tabular-nums text-ink-400">{onglet.compte}</span>
            ) : null}
            {onglet.point === true ? <span className="h-1.5 w-1.5 rounded-full bg-accent-dot" aria-label="À remplir" /> : null}
          </button>
        );
      })}
    </div>
  );
}
